export const DISPLAY_TZ = 'America/Sao_Paulo';
const LOCALE = 'pt-BR';

const HAS_TZ = /([zZ])$|([+-]\d{2}:?\d{2})$/;

export function parseUtc(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const raw = String(value);
  const hasTime = raw.includes('T') || raw.includes(' ');
  const normalized = hasTime && !HAS_TZ.test(raw) ? `${raw.replace(' ', 'T')}Z` : raw;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value, options = {}, fallback = '-') {
  const d = parseUtc(value);
  if (!d) return fallback;
  return d.toLocaleDateString(LOCALE, { timeZone: DISPLAY_TZ, ...options });
}

export function formatTime(value, options = { hour: '2-digit', minute: '2-digit' }, fallback = '-') {
  const d = parseUtc(value);
  if (!d) return fallback;
  return d.toLocaleTimeString(LOCALE, { timeZone: DISPLAY_TZ, ...options });
}

export function formatDateTime(value, fallback = '-') {
  const d = parseUtc(value);
  if (!d) return fallback;
  return `${formatDate(d)} ${formatTime(d)}`;
}

export function formatDateTimeSeconds(value, fallback = '-') {
  const d = parseUtc(value);
  if (!d) return fallback;
  return formatDate(d) + ' ' + formatTime(d, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function formatDayLabel(ymd, options = { day: '2-digit', month: '2-digit' }, fallback = '-') {
  if (!ymd) return fallback;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd));
  if (!m) return fallback;
  const local = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return local.toLocaleDateString(LOCALE, options);
}

export function toSaoPauloDateString(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DISPLAY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const y = parts.find((p) => p.type === 'year').value;
  const mo = parts.find((p) => p.type === 'month').value;
  const d = parts.find((p) => p.type === 'day').value;
  return `${y}-${mo}-${d}`;
}

function spWallClockToUtc(y, mo, d, h, mi, s, ms = 0) {
  const guessUtc = Date.UTC(y, mo - 1, d, h, mi, s, ms);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: DISPLAY_TZ,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const p = dtf.formatToParts(new Date(guessUtc)).reduce((a, x) => {
    a[x.type] = x.value;
    return a;
  }, {});
  const hour = p.hour === '24' ? 0 : Number(p.hour);
  const tzAsUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), Number(p.second), ms);
  return new Date(guessUtc - (tzAsUtc - guessUtc));
}

export function saoPauloDayStartUtcIso(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd));
  if (!m) return undefined;
  return spWallClockToUtc(Number(m[1]), Number(m[2]), Number(m[3]), 0, 0, 0, 0).toISOString();
}

export function saoPauloDayEndUtcIso(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd));
  if (!m) return undefined;
  return spWallClockToUtc(Number(m[1]), Number(m[2]), Number(m[3]), 23, 59, 59, 999).toISOString();
}

const LOCAL_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

export function utcIsoToSaoPauloInput(value) {
  const d = parseUtc(value);
  if (!d) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: DISPLAY_TZ,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
    .formatToParts(d)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
  const hour = p.hour === '24' ? '00' : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
}

export function saoPauloInputToUtcIso(value) {
  const m = LOCAL_INPUT_PATTERN.exec(String(value ?? ''));
  if (!m) return undefined;
  return spWallClockToUtc(
    Number(m[1]),
    Number(m[2]),
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    0,
    0,
  ).toISOString();
}

export function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}
